import { GraduationCap, PiggyBank, Home, Scale, type LucideIcon } from 'lucide-react'

/**
 * Track config — הגדרות מסלולים
 *
 * The frontend half of the track system. Mirrors backend/src/models/track.types.ts.
 *
 * The KEY SET lives in code (not the API) on purpose: a track needs a route, an
 * entry page, an icon and a landing card regardless, and a literal union is what
 * makes TypeScript point at every place a new track has to be threaded through.
 * Everything *about* a track — name, hero copy, whether it is open — comes from
 * the server and is admin-editable; the values here are only the fallbacks used
 * before that request resolves.
 *
 * Adding a fourth track: one entry in TRACK_KEYS, one in TRACKS, one seed file
 * and one PortalTrack row. No new components.
 */

export const TRACK_KEYS = ['student', 'retirement', 'mortgage'] as const
export type TrackKey = (typeof TRACK_KEYS)[number]

/** Rules and feedback belong either to a track or to the advisory product. */
export type RuleScope = TrackKey | 'advisory'

export interface TrackUiConfig {
  key: TrackKey
  /** URL root, e.g. '/pensia'. Must match PortalTrack.urlSlug on the server. */
  basePath: string
  name: string
  shortName: string
  tagline: string
  Icon: LucideIcon
  accent: string
  hasExam: boolean
  /** Where an anonymous visitor signs in / signs up for this track. */
  loginPath: string
  signupPath: string
  /** Wording for the spent-allowance message — the student one mentions the exam. */
  capReachedMessage: string
}

export const TRACKS: Record<TrackKey, TrackUiConfig> = {
  student: {
    key: 'student',
    basePath: '/learn',
    name: 'פורטל סטודנטים',
    shortName: 'סטודנטים',
    tagline: 'תוכנית חינוכית להיכרות עם עולם ההשקעות',
    Icon: GraduationCap,
    accent: 'purple',
    hasExam: true,
    loginPath: '/student-login',
    signupPath: '/register-student',
    capReachedMessage:
      'סיימת את מכסת השיחה החינמית שלך עם המדריך. אפשר להמשיך ללמוד את כל הנושאים ולגשת למבחן המסכם — ולבקש מהמורה לאפס את המכסה.',
  },
  retirement: {
    key: 'retirement',
    basePath: '/pensia',
    name: 'תכנון פרישה',
    shortName: 'פרישה',
    tagline: 'להבין את הפנסיה שלך לפני שמחליטים עליה',
    Icon: PiggyBank,
    accent: 'emerald',
    hasExam: false,
    loginPath: '/login?next=/pensia',
    signupPath: '/pensia/signup',
    capReachedMessage:
      'סיימת את מכסת השיחה החינמית באזור הזה. אפשר להמשיך לקרוא את כל התוכן — ולפנות אלינו כדי להמשיך לשוחח.',
  },
  mortgage: {
    key: 'mortgage',
    basePath: '/mashkanta',
    name: 'משכנתא',
    shortName: 'משכנתא',
    tagline: 'להבין את ההלוואה הגדולה בחיים שלך',
    Icon: Home,
    accent: 'amber',
    hasExam: false,
    loginPath: '/login?next=/mashkanta',
    signupPath: '/mashkanta/signup',
    capReachedMessage:
      'סיימת את מכסת השיחה החינמית באזור הזה. אפשר להמשיך לקרוא את כל התוכן — ולפנות אלינו כדי להמשיך לשוחח.',
  },
}

/**
 * מאזן — the one-time-paid איזון משאבים report. NOT a learning track (no
 * content, no AI, no PortalTrack row), so it is not in TRACK_KEYS; it only
 * shares the light portal-only account (User.portalAccess 'mazan').
 */
export const MAZAN = {
  key: 'mazan' as const,
  basePath: '/Mazan',
  homePath: '/Mazan/my',
  name: 'איזון משאבים',
  shortName: 'איזון',
  tagline: 'תחשיב איזון משאבים לפנסיה מקיפה ומשלימה',
  Icon: Scale as LucideIcon,
  loginPath: '/login?next=/Mazan/my',
  signupPath: '/Mazan/signup',
}

/** Runs the מאזן area in the admin panel: full admins, and a sub-admin assigned 'mazan'. */
export function managesMazan(user?: PortalUserLike | null): boolean {
  if (user?.role === 'admin') return true
  return user?.role === 'sub_admin' && !!user.managedTracks?.includes(MAZAN.key)
}

export function hasMazanAccess(user?: PortalUserLike | null): boolean {
  return !!user?.portalAccess?.some((p) => p.track === MAZAN.key)
}

/** The two tracks anyone can join with a plain email + password. */
export const PUBLIC_TRACK_KEYS: TrackKey[] = ['retirement', 'mortgage']

/** The subset of a user we need to decide which chrome they get. */
export interface PortalUserLike {
  role?: string
  plan?: string
  agreementSigned?: boolean
  subscription?: { status?: string } | null
  portalAccess?: { track: string }[]
  /** Sections a 'sub_admin' administers. */
  managedTracks?: string[]
}

/**
 * Staff — admin, advisor, and the section admin (מנהל אזור) who runs one area's
 * content and AI. None of them are advisory clients, so none of them meet the
 * agreement → questionnaire → risk-approval chain.
 */
export function isStaffRole(role?: string): boolean {
  return role === 'admin' || role === 'advisor' || role === 'sub_admin'
}

/** The sections this user administers, in TRACK order. Empty for everyone else. */
export function managedTracks(user?: PortalUserLike | null): TrackKey[] {
  if (user?.role !== 'sub_admin') return []
  return TRACK_KEYS.filter((key) => user.managedTracks?.includes(key))
}

/** The open areas this user has actually joined, in TRACK order. */
export function joinedPublicTracks(user?: PortalUserLike | null): TrackKey[] {
  return PUBLIC_TRACK_KEYS.filter((key) => user?.portalAccess?.some((p) => p.track === key))
}

/**
 * Portal-only user — משתמש אזור פתוח בלבד.
 *
 * Someone who came in through /pensia or /mashkanta and never engaged with the
 * advisory product: no signed agreement, free plan, no subscription. They are
 * role 'client' like everyone else — the free tracks deliberately did NOT invent
 * a role — so the distinction has to be made here, from what the account has
 * actually done.
 *
 * The app treats them like students: their own area, profile, install, sign out.
 * The advisory chrome (לוח בקרה, שאלון סיכון, צ'אט ייעוץ, המנוי שלי) is not
 * theirs to use and only confuses them. The moment they sign the agreement or
 * subscribe, this returns false and the full app comes back on its own.
 */
export function isPortalOnlyUser(user?: PortalUserLike | null): boolean {
  if (!user) return false
  if (isStaffRole(user.role) || user.role === 'student') return false

  const engagedWithAdvisory =
    user.agreementSigned === true ||
    user.plan === 'pro' ||
    user.subscription?.status === 'active' ||
    user.subscription?.status === 'past_due'
  if (engagedWithAdvisory) return false

  return joinedPublicTracks(user).length > 0 || hasMazanAccess(user)
}

/**
 * Where a portal-only user belongs after signing in — their own area, not the
 * advisory dashboard (which would bounce them into the advisory agreement).
 * Returns null for everyone else.
 */
export function portalHomePathFor(user?: PortalUserLike | null): string | null {
  if (!isPortalOnlyUser(user)) return null
  const [first] = joinedPublicTracks(user)
  if (first) return TRACKS[first].basePath
  return hasMazanAccess(user) ? MAZAN.homePath : null
}

export function isTrackKey(value: unknown): value is TrackKey {
  return typeof value === 'string' && (TRACK_KEYS as readonly string[]).includes(value)
}

/** Resolve a track from a URL path, e.g. '/pensia/topic/x' → 'retirement'. */
export function trackFromPath(pathname: string): TrackKey | null {
  const match = Object.values(TRACKS).find(
    (t) => pathname === t.basePath || pathname.startsWith(`${t.basePath}/`)
  )
  return match?.key ?? null
}
